import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  telefone: z.string().min(14, 'Telefone deve ter pelo menos 10 dígitos'),
});

type FormData = z.infer<typeof formSchema>;

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName?: string;
}

export default function WaitlistModal({ isOpen, onClose, planName }: WaitlistModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
    },
  });

  const formatPhoneNumber = (value: string) => {
    let isInternational = false;
    if (value.startsWith('+')) {
      isInternational = true;
      value = '+' + value.slice(1).replace(/\D/g, '');
    } else {
      value = value.replace(/\D/g, '');
    }

    if (value.length === 0) {
      return '';
    }

    if (isInternational || value.length > 11) {
      return isInternational ? `${value.slice(0, 20)}` : value.slice(0, 20);
    }

    if (value.length <= 2) {
      return `${value}`;
    }
    if (value.length <= 6) {
      return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    }
    if (value.length <= 10) {
      return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
    }
    if (value.length <= 11) {
      return `(${value.slice(0, 2)}) ${value.slice(2, 3)} ${value.slice(3, 7)}-${value.slice(7)}`;
    }
    return value;
  };

  const handlePhoneChange = (value: string, onChange: (value: string) => void) => {
    const formattedValue = formatPhoneNumber(value);
    onChange(formattedValue);
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('email', data.email);
      formData.append('telefone', data.telefone);
      formData.append('tenant_id', '13772');
      formData.append('form_id', '33');

      await axios.post('https://back.gdigital.com.br/form/register', formData);
      
      onClose();
      navigate('/obrigado-espera-copy');
    } catch (error) {
      setSubmitError('Erro ao cadastrar. Tente novamente.');
      console.error('Erro no envio:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-cyan-400 text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-white">
            Lista de Espera Copy Trade
          </DialogTitle>
          {planName && (
            <div className="text-center text-cyan-400 font-semibold text-lg mb-2">
              {planName}
            </div>
          )}
          <DialogDescription className="text-center text-slate-300">
            Cadastre-se na lista de prioridade e seja o primeiro a saber quando abrirmos novas vagas!
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">Nome *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Seu nome completo" 
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-400"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">E-mail *</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="seu@email.com" 
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-400"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-200">WhatsApp *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="(11) 9 9999-9999"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-400"
                      value={field.value}
                      onChange={(e) => handlePhoneChange(e.target.value, field.onChange)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {submitError && (
              <div className="text-sm text-destructive text-center bg-destructive/10 p-2 rounded">
                {submitError}
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-600 hover:to-green-600 text-black shadow-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                'Cadastrar na Lista de Espera'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}